/**
 * Turnstile widget wrapper.
 *
 * Uses the default `render` execution mode, so the challenge runs at page load
 * and the token is ready before submit — keeping it off the critical path
 * (PERFORMANCE.md §2.5). The script is loaded straight from Cloudflare and must
 * never be proxied or cached.
 */
import { useEffect, useRef, useState } from 'react';

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
const SCRIPT_ID = 'cf-turnstile-script';

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: {
          sitekey: string;
          callback: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
          theme?: 'light' | 'dark' | 'auto';
        },
      ) => string;
      remove: (id: string) => void;
      reset: (id: string) => void;
    };
  }
}

let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (window.turnstile?.render) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    // Use Turnstile's own onload callback rather than the script element's
    // `load` event. The script fires `load` before `window.turnstile` is
    // populated, so resolving on `load` produces a race where render() is
    // called against an undefined API and silently no-ops — the widget never
    // appears and no token is ever issued.
    const CB = '__ideabriefTurnstileReady';
    (window as unknown as Record<string, unknown>)[CB] = () => resolve();

    if (document.getElementById(SCRIPT_ID)) {
      // Another instance is already loading it; poll briefly for readiness.
      const started = Date.now();
      const tick = () => {
        if (window.turnstile?.render) return resolve();
        if (Date.now() - started > 10_000) return reject(new Error('turnstile'));
        setTimeout(tick, 50);
      };
      tick();
      return;
    }

    const s = document.createElement('script');
    s.id = SCRIPT_ID;
    s.src = `${SCRIPT_URL}?onload=${CB}&render=explicit`;
    s.async = true;
    s.defer = true;
    s.onerror = () => reject(new Error('turnstile'));
    document.head.appendChild(s);
  });

  return scriptPromise;
}

interface TurnstileProps {
  siteKey: string;
  onToken: (token: string) => void;
  /** Bump to force a fresh token — tokens are single-use. */
  resetKey?: number;
}

export function Turnstile({ siteKey, onToken, resetKey = 0 }: TurnstileProps) {
  const ref = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  const [failed, setFailed] = useState(false);
  // Keeps the effect from re-running when the parent passes a new closure.
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  useEffect(() => {
    if (!siteKey) return;
    let cancelled = false;

    loadScript()
      .then(() => {
        if (cancelled || !ref.current) return;
        if (!window.turnstile?.render) {
          // Should be unreachable now that we wait on Turnstile's own onload,
          // but fail visibly rather than rendering nothing.
          setFailed(true);
          return;
        }
        widgetId.current = window.turnstile.render(ref.current, {
          sitekey: siteKey,
          callback: (token) => onTokenRef.current(token),
          'expired-callback': () => onTokenRef.current(''),
          'error-callback': () => {
            onTokenRef.current('');
            setFailed(true);
          },
        });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (widgetId.current && window.turnstile) {
        window.turnstile.remove(widgetId.current);
        widgetId.current = null;
      }
    };
  }, [siteKey, resetKey]);

  if (failed) {
    return (
      <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--muted)' }}>
        Verification couldn't load. Check your connection and refresh the page.
      </p>
    );
  }

  return <div ref={ref} style={{ marginBottom: 14 }} />;
}
